import { computed, shallowRef, watchEffect } from "vue";
import { getElement } from "@/services/dom";

export interface Address {
	street?: string;
	city?: string;
	zip_code?: string;
	country_code?: string;
	latitude?: number | string;
	longitude?: number | string;
}

export const form = shallowRef<Address | null>({
	street: "",
	city: "",
	zip_code: "",
	country_code: ""
});

export const isFormValid = computed(() => !!form.value && Object.values(form.value).some((value) => value !== ""));
export const isFormValidWithoutCountryCode = computed(() => {
	const { street, city, zip_code } = form.value!;
	return !!form.value && (street !== "" || city !== "" || zip_code !== "");
});

const billingFields = {
	address_1: '#billing-address_1, #billing_address_1, [name="billing_address_1"]',
	address_2: '#billing-address_2, #billing_address_2, [name="billing_address_2"]',
	city: '#billing-city, #billing_city, [name="billing_city"]',
	zipcode: '#billing-postcode, #billing_postcode, [name="billing_postcode"]',
	country: '#billing-country, #billing_country, [name="billing_country"]',
	state: '#billing-state, #billing_state, [name="billing_state"]'
};

const shippingFields = {
	address_1: '#shipping-address_1, #shipping_address_1, [name="shipping_address_1"]',
	address_2: '#shipping-address_2, #shipping_address_2, [name="shipping_address_2"]',
	city: '#shipping-city, #shipping_city, [name="shipping_city"]',
	zipcode: '#shipping-postcode, #shipping_postcode, [name="shipping_postcode"]',
	country: '#shipping-country, #shipping_country, [name="shipping_country"]',
	state: '#shipping-state, #shipping_state, [name="shipping_state"]'
};

const addressHandler = {
	getAddress(fields: typeof billingFields, addressData?: any): Address {
		if (addressData) {
			const street = [addressData.address_1?.trim() || "", addressData.address_2?.trim() || ""].filter(Boolean).join(", ");
			return {
				street,
				city: addressData.city?.trim() || "",
				zip_code: addressData.postcode?.trim() || "",
				country_code: addressData.country?.trim() || ""
			};
		}
		return {
			street: getElement<HTMLInputElement>(fields.address_1 + ", " + fields.address_2, document)?.value.trim(),
			city: getElement<HTMLInputElement>(fields.city, document)?.value.trim(),
			zip_code: getElement<HTMLInputElement>(fields.zipcode, document)?.value.trim(),
			country_code: getElement<HTMLInputElement>(fields.country, document)?.value.trim()
		};
	},
	getBillingAddress(): Address {
		if (window?.wp?.data) {
			const customerData = window.wp.data.select("wc/store/cart")?.getCustomerData();
			return this.getAddress(billingFields, customerData?.billingAddress);
		}
		return this.getAddress(billingFields);
	},
	getShippingAddress(): Address {
		if (window?.wp?.data) {
			const customerData = window.wp.data.select("wc/store/cart")?.getCustomerData();
			return this.getAddress(shippingFields, customerData?.shippingAddress);
		}
		return this.getAddress(shippingFields);
	},
	getIsShippingSelected(): HTMLInputElement | null {
		if (window?.wp?.data && window.wp.data.select("wc/store/cart")) {
			const select = window.wp.data.select;
			const useShipping = select("wc/store/cart").shippingAsBilling;

			if (typeof useShipping === "boolean") {
				const dummyInput = document.createElement("input");
				dummyInput.type = "checkbox";
				dummyInput.checked = useShipping;
				return dummyInput;
			}

			const { subscribe } = window.wp.data;
			const cartStore = window.wp.data.select("wc/store/cart");

			subscribe(() => {
				const useShipping = cartStore.shippingAsBilling;
				if (typeof useShipping === "boolean") {
					const dummyInput = document.createElement("input");
					dummyInput.type = "checkbox";
					dummyInput.checked = useShipping;
					return dummyInput;
				}
			});
		}

		return getElement<HTMLInputElement>(".wc-block-components-checkbox__input, #ship-to-different-address-checkbox, #checkbox-control-0", document);
	},
	subscribeToAddressChanges() {
		if (window?.wp?.data) {
			const { subscribe } = window.wp.data;

			subscribe(() => {
				const newBillingAddress = this.getBillingAddress();
				const newShippingAddress = this.getShippingAddress();

				if (JSON.stringify(form.value) !== JSON.stringify(newShippingAddress)) {
					form.value = newShippingAddress;
				}

				if (JSON.stringify(form.value) !== JSON.stringify(newBillingAddress)) {
					form.value = newBillingAddress;
				}
			});
		}
	}
};

form.value = addressHandler.getShippingAddress();

const isShippingSelected = addressHandler.getIsShippingSelected();

const updateCurrentAddress = () => {
	if (!isShippingSelected) return;
	form.value = isShippingSelected.checked ? addressHandler.getShippingAddress() : addressHandler.getBillingAddress();
};

isShippingSelected?.addEventListener("change", updateCurrentAddress, true);

Object.values(billingFields).forEach((field) => {
	const input = getElement<HTMLInputElement>(field, document);
	input?.addEventListener("input", updateCurrentAddress, true);
});

Object.values(shippingFields).forEach((field) => {
	const input = getElement<HTMLInputElement>(field, document);
	input?.addEventListener("input", updateCurrentAddress, true);
});

addressHandler.subscribeToAddressChanges();
